/**
 * BudgetFilters Component
 * Filter controls for budget line items
 */

import React from 'react';
import { Search, Filter } from 'lucide-react';
import { Dropdown } from '../forms';
import type { BudgetFilterParams } from '../../types/budget';

export interface BudgetFiltersProps {
  filters: BudgetFilterParams;
  onFilterChange: (filters: BudgetFilterParams) => void;
  categories: string[];
  divisions: string[];
}

const BudgetFilters: React.FC<BudgetFiltersProps> = ({
  filters,
  onFilterChange,
  categories,
  divisions,
}) => {
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onFilterChange({ ...filters, search: e.target.value });
  };

  const handleCategoryChange = (value: string) => {
    onFilterChange({ ...filters, category: value || undefined });
  };

  const handleDivisionChange = (value: string) => {
    onFilterChange({ ...filters, division: value || undefined });
  };

  const handleZeroBudgetToggle = () => {
    onFilterChange({ ...filters, showZeroBudget: !filters.showZeroBudget });
  };

  const categoryOptions = [
    { value: '', label: 'All Categories' },
    ...categories.map(cat => ({ value: cat, label: cat })),
  ];

  const divisionOptions = [
    { value: '', label: 'All Divisions' },
    ...divisions.map(div => ({ value: div, label: div })),
  ];

  const hasActiveFilters =
    filters.category || filters.division || filters.search || filters.showZeroBudget;

  const clearFilters = () => {
    onFilterChange({
      category: undefined,
      division: undefined,
      search: undefined,
      showZeroBudget: false,
    });
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Filter className="h-5 w-5 text-gray-400" />
          <h3 className="text-sm font-medium text-gray-900">Filters</h3>
          {hasActiveFilters && (
            <span className="text-xs text-brand-600 bg-brand-50 px-2 py-1 rounded">Active</span>
          )}
        </div>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-xs text-brand-600 hover:text-brand-700 font-medium transition-colors"
          >
            Clear All
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search by cost code or description..."
            value={filters.search || ''}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
          />
        </div>

        {/* Category Filter */}
        <Dropdown
          value={filters.category || ''}
          options={categoryOptions}
          onChange={handleCategoryChange}
          placeholder="Select category"
        />

        {/* Division Filter */}
        <Dropdown
          value={filters.division || ''}
          options={divisionOptions}
          onChange={handleDivisionChange}
          placeholder="Select division"
        />

        {/* Show Zero Budget Toggle */}
        <div className="flex items-center">
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.showZeroBudget || false}
              onChange={handleZeroBudgetToggle}
              className="h-4 w-4 text-brand-600 focus:ring-brand-500 border-gray-300 rounded"
            />
            <span className="text-sm text-gray-700">Show zero budget items</span>
          </label>
        </div>
      </div>
    </div>
  );
};

export default BudgetFilters;
